import type { WarehouseSync } from '@/services/db/warehouse-sync/entity';

/**
 * The export toggles a sync can carry. `object_attributes` is snake_case on both
 * the wire and here so the id can index the record directly.
 */
export const WarehouseDataOptionID = {
  deliveries: 'deliveries',
  contents: 'contents',
  metrics: 'metrics',
  subjects: 'subjects',
  outputs: 'outputs',
  people: 'people',
  attributes: 'attributes',
  campaigns: 'campaigns',
  broadcasts: 'broadcasts',
  objects: 'objects',
  objectAttributes: 'object_attributes',
  events: 'events',
  inbound: 'inbound',
} as const;

export type WarehouseDataOptionID = (typeof WarehouseDataOptionID)[keyof typeof WarehouseDataOptionID];

export type WarehouseDataOption = {
  id: WarehouseDataOptionID;
  name: string;
  description: string;
};

/** The taxonomy terms the option copy interpolates. */
export type WarehouseDataOptionTerms = {
  peoplePlural: string;
  peopleTitlePlural: string;
  personSingular: string;
  campaignSingular: string;
  campaignTitleSingular: string;
};

export const DEFAULT_OPTION_TERMS: WarehouseDataOptionTerms = {
  peoplePlural: 'people',
  peopleTitlePlural: 'People',
  personSingular: 'person',
  campaignSingular: 'campaign',
  campaignTitleSingular: 'Campaign',
};

/**
 * Every export option, in the order the details table renders them. Note this is
 * render order, not the declaration order of the ids above — `contents` sits
 * second here.
 */
export function getWarehouseDataOptions(terms: WarehouseDataOptionTerms = DEFAULT_OPTION_TERMS): WarehouseDataOption[] {
  return [
    {
      id: WarehouseDataOptionID.deliveries,
      name: 'Deliveries',
      description: 'One record for each email, push, SMS, Slack, or webhook message created.',
    },
    {
      id: WarehouseDataOptionID.contents,
      name: 'Delivery contents',
      description:
        'One record for each email, push, SMS, Slack, or webhook message created. Each record contains the actual content sent out.',
    },
    {
      id: WarehouseDataOptionID.metrics,
      name: 'Metrics',
      description: 'One record for each event that occurs for a message (eg. delivered, bounced, opened, clicked).',
    },
    {
      id: WarehouseDataOptionID.subjects,
      name: 'Subjects',
      description: 'One record for each workflow journey started.',
    },
    {
      id: WarehouseDataOptionID.outputs,
      name: 'Outputs',
      description: 'One record for each discrete step in a workflow journey.',
    },
    {
      id: WarehouseDataOptionID.people,
      name: terms.peopleTitlePlural,
      description: `A list of ${terms.peoplePlural} IDs and if they've been deleted or suppressed.`,
    },
    {
      id: WarehouseDataOptionID.attributes,
      name: 'Attributes',
      description: `One record for each attribute on a ${terms.personSingular}.`,
    },
    {
      id: WarehouseDataOptionID.campaigns,
      name: `${terms.campaignTitleSingular} metadata`,
      description: `One record for each change to a ${terms.campaignSingular} name or ${terms.campaignSingular} action name.`,
    },
    {
      id: WarehouseDataOptionID.broadcasts,
      name: 'Broadcasts metadata',
      description: 'One record for each change to a broadcast or newsletter.',
    },
    {
      id: WarehouseDataOptionID.objects,
      name: 'Objects',
      description: 'A list of object and object type IDs.',
    },
    {
      id: WarehouseDataOptionID.objectAttributes,
      name: 'Object attributes',
      description: 'One record for each attribute change on an object.',
    },
    {
      id: WarehouseDataOptionID.events,
      name: 'Events',
      description: `One record for each event associated with a ${terms.personSingular}.`,
    },
    {
      id: WarehouseDataOptionID.inbound,
      name: 'Inbound',
      description: `One record for each inbound message received by a ${terms.personSingular}.`,
    },
  ];
}

const V1_IDS: readonly WarehouseDataOptionID[] = ['deliveries', 'metrics', 'people'];
const V2_V3_IDS: readonly WarehouseDataOptionID[] = ['deliveries', 'metrics', 'people', 'attributes'];
const V4_IDS: readonly WarehouseDataOptionID[] = [
  'deliveries',
  'contents',
  'metrics',
  'subjects',
  'outputs',
  'people',
  'attributes',
  'campaigns',
  'broadcasts',
  'objects',
  'object_attributes',
  'events',
];
const V5_IDS: readonly WarehouseDataOptionID[] = [...V4_IDS, 'inbound'];

/** The option ids a given schema version exports. v6+ add columns, not files, so they match v5. */
export function schemaVersionOptionIds(version: number): readonly WarehouseDataOptionID[] {
  if (version === 1) return V1_IDS;
  if (version <= 3) return V2_V3_IDS;
  if (version === 4) return V4_IDS;
  return V5_IDS;
}

function optionsForIds(ids: readonly WarehouseDataOptionID[], terms: WarehouseDataOptionTerms): WarehouseDataOption[] {
  return getWarehouseDataOptions(terms).filter((option) => ids.includes(option.id));
}

/**
 * The rows the details table shows. Deliberately keyed off v4/v5 rather than the
 * sync's own version: an older sync still lists every v4 option, and the rows it
 * cannot export yet are the ones that offer the upgrade.
 */
export function getVisibleWarehouseOptions({
  version,
  isTwilioEnabled,
  terms = DEFAULT_OPTION_TERMS,
}: {
  version: number;
  isTwilioEnabled: boolean;
  terms?: WarehouseDataOptionTerms;
}): WarehouseDataOption[] {
  const ids = version >= 5 ? V5_IDS : V4_IDS;
  const options = optionsForIds(ids, terms);
  // `inbound` needs both a v5 schema and the Twilio action; without it the row is
  // hidden rather than shown disabled.
  if (version >= 5 && !isTwilioEnabled) {
    return options.filter((option) => option.id !== WarehouseDataOptionID.inbound);
  }
  return options;
}

/**
 * The options an upgrade to the latest schema would newly export — what the
 * upgrade modal offers. Empty for a workspace already on v5+.
 */
export function getNewlyAvailableOptions({
  version,
  isTwilioEnabled,
  terms = DEFAULT_OPTION_TERMS,
}: {
  version: number;
  isTwilioEnabled: boolean;
  terms?: WarehouseDataOptionTerms;
}): WarehouseDataOption[] {
  const existing = schemaVersionOptionIds(version);
  const newOptions = optionsForIds(V5_IDS, terms).filter((option) => !existing.includes(option.id));
  if (!isTwilioEnabled) {
    return newOptions.filter((option) => option.id !== WarehouseDataOptionID.inbound);
  }
  return newOptions;
}

/**
 * Whether this option is one the sync's schema cannot export yet, so its row
 * offers the upgrade instead of a toggle.
 *
 * Compared by id. Ember compared option *objects* against module-level frozen
 * arrays, which silently never matched once taxonomy-flavoured options were
 * passed in — the rows it was meant to disable rendered as normal toggles.
 */
export function optionNeedsSchemaUpgrade(version: number, optionId: WarehouseDataOptionID): boolean {
  if (version >= 4) return false;
  return !schemaVersionOptionIds(version).includes(optionId);
}

/** A sync is paused for display when every *visible* toggle is off. */
export function isWarehouseSyncPausedForDisplay(sync: WarehouseSync, visible: readonly WarehouseDataOption[]): boolean {
  return visible.every((option) => sync[option.id] === false);
}
